// Document Builder — the multi-mode window-pane document workspace.
//
// A real, editable multi-pane document surface backed by the existing
// sq_workspaces engine (api.workspace list/get/create/run/events):
//   • Mode tabs across the top (Document / PDF / Slides / Sheets /
//     Notes) — each maps to a REAL workspace mode the create/run
//     endpoints accept; the active mode drives accent + build mode.
//   • Doc / Split / Preview window panes (WorkspaceDualPane).
//   • Sections render as inline-editable blocks (local working copy).
//   • "Build" dispatches a REAL governed workspace run — no fake
//     timeline; the run + streamed sections + agent events come back
//     from the server.
//   • The right rail shows real agent activity (workspace events) and
//     live operator signal.
//
// Emptiness fix (operator complaint — "too empty/thin"): the surface
// no longer collapses to a bare list. When no workspace is selected we
// show the workspace cards PLUS a structured, clearly DEMO-labeled
// preview of the composed document. When a workspace is open but has
// no real sections yet, the panes render the rich EXAMPLE document
// structure for the active mode — every demo section carries a DEMO
// chip so nothing masquerades as real output.
//
// Honesty contract (matches the workspace design system):
//   • inline edits are a LOCAL draft (chip says so) — there is no
//     section-write endpoint yet, so nothing claims to persist;
//   • an "AI edit" on a section STAGES an approval-required request in
//     the rail — it never rewrites the text or calls a provider;
//   • the example document + demo agent feed are DEMO-labeled sample
//     content, never presented as real sections or real events;
//   • export chips are honestly REQUIRES-SETUP — no export endpoint
//     exists yet, so no chip performs a fake export;
//   • attachment chips are honestly blocked (intake not wired);
//   • no provider action fires on render; every empty/blocked state is
//     honest and named.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Banner,
  EmptyState,
  InlineAlert,
  WorkspaceCommandShell,
  WorkspaceDualPane,
  WorkspaceInlineEditable,
  WorkspaceModuleCard,
  WorkspaceDocumentPane,
  WorkspaceDocSection,
  WorkspaceActionComposer,
  WorkspaceProofPanel,
  WorkspaceRightRail,
  WorkspaceRailSection,
  WorkspaceNotificationRail,
  WorkspaceStatusChip,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_TEXT,
  WORKSPACE_SURFACE,
  workspaceTint,
} from '@square/ui'
import { api, ApiError, isAuthError, type WorkspaceApiRow, type WorkspaceEventApiRow } from '../../../api/client'
import { useWorkspaceSignals } from '../../../hooks/useWorkspaceSignals'

// ── Mode config — every key is a REAL workspace mode the API accepts ──
type ModeKey = 'document' | 'pdf' | 'presentation' | 'spreadsheet' | 'notes'

interface ModeDef {
  key: ModeKey
  label: string
  glyph: string
  accent: string
  ext: string
}

const MODES: ModeDef[] = [
  { key: 'document', label: 'Document', glyph: '◼', accent: WORKSPACE_ACCENTS.blue, ext: '.docx' },
  { key: 'pdf', label: 'PDF', glyph: '◆', accent: WORKSPACE_ACCENTS.purple, ext: '.pdf' },
  { key: 'presentation', label: 'Slides', glyph: '▶', accent: WORKSPACE_ACCENTS.red, ext: '.pptx' },
  { key: 'spreadsheet', label: 'Sheets', glyph: '▦', accent: WORKSPACE_ACCENTS.green, ext: '.xlsx' },
  { key: 'notes', label: 'Notes', glyph: '✎', accent: WORKSPACE_ACCENTS.gold, ext: '.md' },
]
const MODE_BY_KEY: Record<string, ModeDef> = Object.fromEntries(MODES.map((m) => [m.key, m]))

// Demo palette — mapped onto the shared workspace accents so sample
// content belongs to the same visual system.
const C = {
  CYAN: WORKSPACE_ACCENTS.cyan,
  BLUE: WORKSPACE_ACCENTS.blue,
  GREEN: WORKSPACE_ACCENTS.green,
  RED: WORKSPACE_ACCENTS.red,
  PURPLE: WORKSPACE_ACCENTS.purple,
  AMBER: WORKSPACE_ACCENTS.gold,
  PINK: WORKSPACE_ACCENTS.pink,
}
const DEMO_AGENTS: Record<string, string> = {
  ATHENA: C.CYAN,
  MORPHEUS: C.AMBER,
  OBSIDIAN: C.RED,
  APOLLO: C.BLUE,
  AURA: C.PINK,
  ORACLE: C.GREEN,
  ECHO: C.BLUE,
}

interface DemoAgentMsg { agent: string; text: string }
interface DemoModeContent {
  placeholder: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sections: any[]
  agents: DemoAgentMsg[]
}

// ── Sample compositions per mode (DEMO — never real sections) ──
const DEMO_CONTENT: Record<ModeKey, DemoModeContent> = {
  document: {
    placeholder: 'Draft a partnership agreement for Silver Key Realty Q2 expansion…',
    sections: [
      { type: 'title', text: 'Silver Key Realty — Partnership Agreement' },
      { type: 'meta', text: 'Prepared by ATHENA · Strategy Division · March 2026 · CONFIDENTIAL' },
      { type: 'divider' },
      { type: 'heading', text: '1. Parties & Recitals' },
      { type: 'body', agent: 'ATHENA', text: "This Partnership Agreement ('Agreement') is entered into as of March 15, 2026, by and between Silver Key Realty LLC, a Texas limited liability company ('Silver Key'), and Meridian Capital Partners, a Delaware limited partnership ('Meridian'). Both parties acknowledge their mutual interest in expanding residential brokerage operations across the Indianapolis metropolitan area." },
      { type: 'heading', text: '2. Purpose & Scope' },
      { type: 'body', agent: 'MORPHEUS', text: "The Partnership shall conduct residential real estate brokerage services utilizing Silver Key's proprietary QUAM platform technology. Meridian provides capital deployment of $2.5M over 18 months, staged in three tranches tied to performance milestones." },
      { type: 'heading', text: '3. Security & Data Protection' },
      { type: 'security', agent: 'OBSIDIAN', text: 'All client data, transaction records, and proprietary algorithms protected under OLIVIUM 7-pillar security architecture. Structural enforcement ensures compliance with FTC Safeguards Rule and state-specific real estate data protection requirements. Breach notification within 72 hours.' },
      { type: 'heading', text: '4. Financial Terms' },
      { type: 'stats', agent: 'MORPHEUS', stats: [
        { label: 'Total Capital', value: '$2.5M', color: C.GREEN },
        { label: 'Equity Split', value: '75/25', color: C.BLUE },
        { label: 'Breakeven', value: "Q3 '26", color: C.AMBER },
        { label: 'Target ROI', value: '3.2x', color: C.PURPLE },
      ] },
      { type: 'body', agent: 'MORPHEUS', text: 'Revenue sharing: 75% Silver Key / 25% Meridian until capital return, then 85/15 in perpetuity. Management fee: 2% of deployed capital annually. Silver Key retains all intellectual property rights to the QUAM platform.' },
      { type: 'callout', agent: 'ATHENA', color: C.CYAN, text: 'Recommendation: Execute by March 20 to align with Q2 launch timeline. Agent LOIs expire March 25. Legal review by both counsel required — estimated 48-hour turnaround with pre-negotiated terms attached as Exhibit A.' },
    ],
    agents: [
      { agent: 'ATHENA', text: 'Analyzing partnership parameters across legal and financial frameworks…' },
      { agent: 'ATHENA', text: 'Structuring recitals — pulling Silver Key entity data and Meridian profile' },
      { agent: 'MORPHEUS', text: 'Financial model loaded — $2.5M staged deployment, 3 tranche structure' },
      { agent: 'OBSIDIAN', text: 'Security clause generated — OLIVIUM compliance, FTC Safeguards, breach notification' },
      { agent: 'AURA', text: 'Inserting financial summary cards — 4 key metrics at-a-glance' },
      { agent: 'ORACLE', text: 'ROI projection: 3.2x based on Indianapolis market growth + agent pipeline' },
      { agent: 'ATHENA', text: 'Draft complete. 4 sections + recommendation. Cross-referenced 3 divisions.' },
    ],
  },
  pdf: {
    placeholder: 'Analyze the Henderson property inspection report and flag risk items…',
    sections: [
      { type: 'title', text: 'Henderson Property — Inspection Analysis' },
      { type: 'meta', text: 'AI Analysis Report · Generated from 47-page inspection PDF · March 2026' },
      { type: 'divider' },
      { type: 'heading', text: 'Document Intelligence Summary' },
      { type: 'extraction', agent: 'ORACLE', items: [
        { page: 3, label: 'Foundation Assessment', status: '⚠ CONCERN', color: C.AMBER },
        { page: 7, label: 'Electrical Panel', status: '✓ PASS', color: C.GREEN },
        { page: 12, label: 'Roof Condition', status: '✗ FAIL', color: C.RED },
        { page: 18, label: 'Plumbing System', status: '⚠ CONCERN', color: C.AMBER },
        { page: 23, label: 'HVAC System', status: '✓ PASS', color: C.GREEN },
        { page: 31, label: 'Structural Integrity', status: '✓ PASS', color: C.GREEN },
      ] },
      { type: 'heading', text: 'Critical Findings' },
      { type: 'body', agent: 'ATHENA', text: 'The roof shows significant deterioration in the southeast quadrant — estimated remaining life of 18-24 months. Three layers of shingles detected (maximum 2 per code). Full tear-off and replacement required. Estimated cost: $12,000-$16,000 — a negotiation lever.' },
      { type: 'heading', text: 'Cost-Benefit Analysis' },
      { type: 'stats', agent: 'MORPHEUS', stats: [
        { label: 'Repair Costs', value: '$18-24K', color: C.RED },
        { label: 'Ask Price', value: '$285K', color: C.BLUE },
        { label: 'Fair Value', value: '$262K', color: C.GREEN },
        { label: 'Leverage', value: 'HIGH', color: C.AMBER },
      ] },
      { type: 'callout', agent: 'ATHENA', color: C.CYAN, text: 'Recommendation: Counter at $262K citing roof replacement and foundation evaluation costs. Alternatively, request $23K seller credit at closing. Strong negotiation position — 2 critical items, 2 concerns of 6 major systems.' },
    ],
    agents: [
      { agent: 'ORACLE', text: 'Processing 47-page inspection PDF through Document Intelligence…' },
      { agent: 'ORACLE', text: '6 major systems identified — mapping pass/fail/concern status' },
      { agent: 'ATHENA', text: 'Roof section flagged CRITICAL — 3-layer shingle violation detected' },
      { agent: 'OBSIDIAN', text: 'Compliance check: all findings mapped to local building code requirements' },
      { agent: 'MORPHEUS', text: 'Cost model built — $18-24K total repairs, adjusting fair market value' },
      { agent: 'ATHENA', text: 'Analysis complete. 47 pages processed. 6 systems evaluated. 2 critical flags.' },
    ],
  },
  presentation: {
    placeholder: "Build a 6-slide investor pitch for Silver Key's Series A raise…",
    sections: [
      { type: 'slide-title', slideNum: 1, text: 'Silver Key Realty', subtitle: 'Series A · $2.5M · Residential Real Estate Reinvented' },
      { type: 'slide', slideNum: 2, agent: 'ATHENA', heading: 'The Problem', bullets: [
        'Legacy brokerages optimize for agent volume, not buyer outcomes',
        'Buyers navigate 12+ platforms, 6+ weeks average search time',
        '34% of transactions fall through due to mismatched expectations',
      ] },
      { type: 'slide', slideNum: 3, agent: 'ATHENA', heading: 'Our Solution', bullets: [
        'QUAM-powered matching: 47 data points per buyer session',
        'Buyer-first model — 80% of platform resources serve the buyer',
        '40% faster close times, 23% higher client satisfaction',
      ] },
      { type: 'slide-stats', slideNum: 4, agent: 'MORPHEUS', heading: 'Traction', stats: [
        { label: 'Monthly Closings', value: '14', color: C.GREEN },
        { label: 'Avg Commission', value: '$4.2K', color: C.BLUE },
        { label: 'Gross Margin', value: '78%', color: C.AMBER },
        { label: 'Agent NPS', value: '72', color: C.PURPLE },
      ] },
      { type: 'slide-chart', slideNum: 5, agent: 'MORPHEUS', heading: 'Revenue Trajectory', bars: [
        { label: '2025', value: 0.4, proj: false },
        { label: '2026', value: 1.2, proj: false },
        { label: '2027', value: 3.8, proj: true },
        { label: '2028', value: 8.4, proj: true },
      ] },
      { type: 'slide-ask', slideNum: 6, agent: 'ECHO', heading: 'The Ask', amount: '$2.5M Series A', allocations: [
        { label: 'Agent Recruitment', pct: 45, color: C.BLUE },
        { label: 'Platform Scaling', pct: 30, color: C.GREEN },
        { label: 'Market Penetration', pct: 25, color: C.AMBER },
      ] },
    ],
    agents: [
      { agent: 'ATHENA', text: 'Structuring 6-slide narrative arc: Problem → Solution → Traction → Ask' },
      { agent: 'AURA', text: 'Loading Silver Key slide template — dark theme, accent red' },
      { agent: 'MORPHEUS', text: 'Traction metrics pulled from Finance division — 14 closings, 78% margin' },
      { agent: 'MORPHEUS', text: 'Revenue chart: $0.4M → $8.4M trajectory with projection markers' },
      { agent: 'ECHO', text: 'Investment ask formatted with allocation breakdown and urgency framing' },
      { agent: 'ATHENA', text: 'Deck complete. 6 slides, 3 divisions referenced.' },
    ],
  },
  spreadsheet: {
    placeholder: 'Build a financial model comparing 3 acquisition scenarios for Henderson property…',
    sections: [
      { type: 'title', text: 'Henderson Property — Acquisition Model' },
      { type: 'meta', text: '3-Scenario Analysis · Generated by MORPHEUS · March 2026' },
      { type: 'divider' },
      { type: 'heading', text: 'Scenario Comparison' },
      { type: 'spreadsheet-table', agent: 'MORPHEUS', headers: ['Metric', 'Conservative', 'Base Case', 'Aggressive'], rows: [
        ['Purchase Price', '$285,000', '$270,000', '$255,000'],
        ['Down Payment (20%)', '$57,000', '$54,000', '$51,000'],
        ['Loan Amount', '$228,000', '$216,000', '$204,000'],
        ['Monthly Payment', '$1,524', '$1,444', '$1,364'],
        ['Repair Reserve', '$24,000', '$18,000', '$12,000'],
        ['Total Cash Needed', '$81,000', '$72,000', '$63,000'],
        ['Cash Flow (Monthly)', '$276', '$356', '$436'],
        ['Cap Rate', '5.2%', '6.1%', '7.0%'],
        ['Cash-on-Cash Return', '4.1%', '5.9%', '8.3%'],
      ] },
      { type: 'heading', text: 'Breakeven & Returns' },
      { type: 'stats', agent: 'ORACLE', stats: [
        { label: 'Breakeven', value: 'Month 1', color: C.GREEN },
        { label: '5yr Equity', value: '$94K', color: C.BLUE },
        { label: 'IRR (5yr)', value: '14.2%', color: C.AMBER },
        { label: 'Total Return', value: '2.3x', color: C.PURPLE },
      ] },
      { type: 'callout', agent: 'ATHENA', color: C.CYAN, text: 'Recommendation: Pursue Base Case at $270K. Inspection findings support $15K reduction from ask. Cash-on-cash return of 5.9% with appreciation upside — Henderson corridor showing 8% annual appreciation.' },
    ],
    agents: [
      { agent: 'MORPHEUS', text: 'Building 3-scenario acquisition model from Henderson property data…' },
      { agent: 'MORPHEUS', text: 'Pulling comparable sales: 12 properties within 0.5mi, last 90 days' },
      { agent: 'ORACLE', text: 'Rental income projected: $1,800/mo conservative, $1,950 aggressive' },
      { agent: 'MORPHEUS', text: 'Loan calculations: 6.8% rate, 30-year fixed, 20% down across all scenarios' },
      { agent: 'ORACLE', text: 'IRR model: 14.2% base case with 8% annual appreciation assumption' },
      { agent: 'MORPHEUS', text: 'Model complete. 3 scenarios, 10 metrics.' },
    ],
  },
  notes: {
    placeholder: 'Capture quick notes from the Erica Henderson meeting…',
    sections: [
      { type: 'title', text: 'Meeting Notes — Erica Henderson' },
      { type: 'meta', text: 'Auto-captured · 5:00 PM · QUAM Conference Room' },
      { type: 'divider' },
      { type: 'note-tag', tags: ['Henderson Property', 'Inspection', 'Counter Offer', 'Timeline'] },
      { type: 'note-item', time: '5:02', priority: 'high', text: 'Erica concerned about roof findings — wants full replacement before closing or equivalent credit' },
      { type: 'note-item', time: '5:14', priority: 'high', text: 'Counter offer strategy: Erica prefers $23K seller credit over price reduction — wants to use own contractors' },
      { type: 'note-item', time: '5:21', priority: 'medium', text: 'Timeline: Erica needs to close by April 15 — lease expires April 30, needs 2 weeks for move' },
      { type: 'note-item', time: '5:28', priority: 'high', text: 'Financing: Pre-approved at $275K with First National. Rate lock expires March 28.' },
      { type: 'heading', text: 'Action Items' },
      { type: 'action-items', agent: 'ATHENA', items: [
        { text: 'Submit counter offer at $262K + $23K seller credit by March 10', owner: 'You', due: 'Mar 10', status: 'urgent' },
        { text: 'Schedule structural engineer visit for foundation evaluation', owner: 'You', due: 'Mar 12', status: 'pending' },
        { text: 'Pull 2 comparable listings for weekend showings', owner: 'ORACLE', due: 'Mar 7', status: 'auto' },
        { text: 'Confirm rate lock extension with First National', owner: 'Erica', due: 'Mar 8', status: 'delegated' },
      ] },
      { type: 'callout', agent: 'ECHO', color: C.BLUE, text: 'Next meeting: March 12 at 3:00 PM — review counter offer response and comparable property tour results.' },
    ],
    agents: [
      { agent: 'ECHO', text: 'Structuring meeting notes from voice capture…' },
      { agent: 'ATHENA', text: 'Cross-referencing with Henderson inspection analysis from earlier today' },
      { agent: 'ORACLE', text: 'Rate lock deadline flagged — March 28, 18 days remaining' },
      { agent: 'ATHENA', text: 'Counter offer strategy extracted: $23K credit preferred over price reduction' },
      { agent: 'ATHENA', text: 'Action items generated: 4 items, 2 owners, 3 deadlines' },
      { agent: 'ECHO', text: 'Notes complete. 6 items captured, 4 actions generated, 1 follow-up scheduled.' },
    ],
  },
}

// ── Demo section renderer — ports the source Section switch onto tokens ──
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DemoSectionView({ section, modeColor }: { section: any; modeColor: string }) {
  const agentColor = section.agent ? DEMO_AGENTS[section.agent] || modeColor : modeColor
  const agentTag = section.agent ? (
    <div style={{ fontSize: 8, color: agentColor, fontWeight: 700, marginBottom: 2, letterSpacing: '0.5px', fontFamily: WORKSPACE_FONT.mono }}>{section.agent}</div>
  ) : null
  const agentBorder = section.agent ? { borderLeft: `2px solid ${workspaceTint(agentColor, 30)}`, paddingLeft: 12 } : {}

  switch (section.type) {
    case 'title':
      return <div style={{ fontSize: 20, fontWeight: 800, color: WORKSPACE_TEXT.primary, letterSpacing: '-0.01em', fontFamily: WORKSPACE_FONT.ui }}>{section.text}</div>
    case 'meta':
      return <div style={{ fontSize: 10, color: WORKSPACE_TEXT.muted, marginBottom: 14, fontFamily: WORKSPACE_FONT.mono }}>{section.text}</div>
    case 'divider':
      return <div style={{ height: 1, background: WORKSPACE_SURFACE.line, margin: '8px 0' }} />
    case 'heading':
      return <div style={{ fontSize: 15, fontWeight: 700, color: WORKSPACE_TEXT.secondary, marginTop: 20, marginBottom: 6, fontFamily: WORKSPACE_FONT.ui }}>{section.text}</div>
    case 'body':
      return (
        <div style={{ ...agentBorder, marginBottom: 10 }}>
          {agentTag}
          <div style={{ fontSize: 12.5, color: WORKSPACE_TEXT.tertiary, lineHeight: 1.85, fontFamily: WORKSPACE_FONT.ui }}>{section.text}</div>
        </div>
      )
    case 'security':
      return (
        <div style={{ ...agentBorder, marginBottom: 10 }}>
          {agentTag}
          <div style={{ padding: '10px 14px', borderRadius: 8, background: workspaceTint(C.RED, 6), border: `1px solid ${workspaceTint(C.RED, 12)}` }}>
            <div style={{ fontSize: 8, letterSpacing: '1.5px', color: C.RED, fontWeight: 700, marginBottom: 4, fontFamily: WORKSPACE_FONT.mono }}>SECURITY</div>
            <div style={{ fontSize: 12, color: WORKSPACE_TEXT.tertiary, lineHeight: 1.7, fontFamily: WORKSPACE_FONT.ui }}>{section.text}</div>
          </div>
        </div>
      )
    case 'stats':
      return (
        <div style={{ ...agentBorder, marginBottom: 10 }}>
          {agentTag}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, margin: '6px 0' }}>
            {section.stats.map((s: { label: string; value: string; color: string }, i: number) => (
              <div key={i} style={{ padding: '12px 6px', borderRadius: 8, textAlign: 'center', background: workspaceTint(s.color, 6), border: `1px solid ${workspaceTint(s.color, 15)}` }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 7, color: WORKSPACE_TEXT.muted, marginTop: 2, fontWeight: 600, letterSpacing: '0.4px', fontFamily: WORKSPACE_FONT.mono }}>{s.label.toUpperCase()}</div>
              </div>
            ))}
          </div>
        </div>
      )
    case 'callout':
      return (
        <div style={{ padding: '14px 16px', borderRadius: 8, background: workspaceTint(section.color || modeColor, 7), borderLeft: `3px solid ${section.color || modeColor}`, marginTop: 14 }}>
          {agentTag}
          <div style={{ fontSize: 8, letterSpacing: '1.5px', color: section.color || modeColor, fontWeight: 700, marginBottom: 4, fontFamily: WORKSPACE_FONT.mono }}>RECOMMENDATION</div>
          <div style={{ fontSize: 12, color: WORKSPACE_TEXT.secondary, lineHeight: 1.8, fontFamily: WORKSPACE_FONT.ui }}>{section.text}</div>
        </div>
      )
    case 'extraction':
      return (
        <div style={{ ...agentBorder, marginBottom: 10 }}>
          {agentTag}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {section.items.map((item: { page: number; label: string; status: string; color: string }, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 8, color: WORKSPACE_TEXT.muted, fontWeight: 600, width: 32, fontFamily: WORKSPACE_FONT.mono }}>p.{item.page}</span>
                  <span style={{ fontSize: 11, color: WORKSPACE_TEXT.secondary, fontWeight: 500 }}>{item.label}</span>
                </div>
                <span style={{ fontSize: 8, fontWeight: 700, color: item.color, padding: '2px 6px', borderRadius: 4, background: workspaceTint(item.color, 12) }}>{item.status}</span>
              </div>
            ))}
          </div>
        </div>
      )
    case 'slide-title':
      return (
        <div style={{ padding: '28px 24px', borderRadius: 12, background: `linear-gradient(135deg, ${workspaceTint(C.RED, 12)}, ${workspaceTint(C.RED, 4)})`, border: `1px solid ${workspaceTint(C.RED, 18)}`, textAlign: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 8, color: workspaceTint(C.RED, 80), fontWeight: 700, letterSpacing: '2px', marginBottom: 6, fontFamily: WORKSPACE_FONT.mono }}>SLIDE {section.slideNum}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: WORKSPACE_TEXT.primary }}>{section.text}</div>
          <div style={{ fontSize: 11, color: WORKSPACE_TEXT.tertiary, marginTop: 4 }}>{section.subtitle}</div>
        </div>
      )
    case 'slide':
      return (
        <div style={{ padding: '16px 20px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', marginBottom: 8 }}>
          {agentTag}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 8, color: workspaceTint(C.RED, 80), fontWeight: 700, letterSpacing: '1px', fontFamily: WORKSPACE_FONT.mono }}>SLIDE {section.slideNum}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: WORKSPACE_TEXT.secondary }}>{section.heading}</span>
          </div>
          {section.bullets.map((b: string, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, paddingLeft: 4 }}>
              <span style={{ color: C.RED, fontSize: 6, marginTop: 5 }}>●</span>
              <span style={{ fontSize: 12, color: WORKSPACE_TEXT.tertiary, lineHeight: 1.7 }}>{b}</span>
            </div>
          ))}
        </div>
      )
    case 'slide-stats':
      return (
        <div style={{ padding: '16px 20px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', marginBottom: 8 }}>
          {agentTag}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 8, color: workspaceTint(C.RED, 80), fontWeight: 700, letterSpacing: '1px', fontFamily: WORKSPACE_FONT.mono }}>SLIDE {section.slideNum}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: WORKSPACE_TEXT.secondary }}>{section.heading}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {section.stats.map((s: { label: string; value: string; color: string }, i: number) => (
              <div key={i} style={{ padding: '14px 6px', borderRadius: 8, textAlign: 'center', background: workspaceTint(s.color, 6), border: `1px solid ${workspaceTint(s.color, 15)}` }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 7, color: WORKSPACE_TEXT.muted, marginTop: 2, fontWeight: 600, letterSpacing: '0.4px', fontFamily: WORKSPACE_FONT.mono }}>{s.label.toUpperCase()}</div>
              </div>
            ))}
          </div>
        </div>
      )
    case 'slide-chart':
      return (
        <div style={{ padding: '16px 20px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', marginBottom: 8 }}>
          {agentTag}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 8, color: workspaceTint(C.RED, 80), fontWeight: 700, letterSpacing: '1px', fontFamily: WORKSPACE_FONT.mono }}>SLIDE {section.slideNum}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: WORKSPACE_TEXT.secondary }}>{section.heading}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 70, padding: '0 8px' }}>
            {section.bars.map((b: { label: string; value: number; proj: boolean }, i: number) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: b.proj ? C.AMBER : C.GREEN, marginBottom: 3 }}>${b.value}M</div>
                <div style={{ height: `${(b.value / 8.4) * 50}px`, borderRadius: 4, background: `linear-gradient(180deg, ${b.proj ? C.AMBER : C.GREEN}, ${workspaceTint(b.proj ? C.AMBER : C.GREEN, 50)})`, border: b.proj ? `1px dashed ${workspaceTint(C.AMBER, 40)}` : 'none' }} />
                <div style={{ fontSize: 8, color: WORKSPACE_TEXT.muted, marginTop: 3 }}>{b.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 8 }}>
            <span style={{ fontSize: 8, color: WORKSPACE_TEXT.muted }}><span style={{ color: C.GREEN }}>■</span> Actual</span>
            <span style={{ fontSize: 8, color: WORKSPACE_TEXT.muted }}><span style={{ color: C.AMBER }}>■</span> Projected</span>
          </div>
        </div>
      )
    case 'slide-ask':
      return (
        <div style={{ padding: 20, borderRadius: 10, background: `linear-gradient(135deg, ${workspaceTint(C.BLUE, 8)}, ${workspaceTint(C.BLUE, 2)})`, border: `1px solid ${workspaceTint(C.BLUE, 18)}`, marginBottom: 8 }}>
          {agentTag}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 8, color: workspaceTint(C.RED, 80), fontWeight: 700, letterSpacing: '1px', fontFamily: WORKSPACE_FONT.mono }}>SLIDE {section.slideNum}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: WORKSPACE_TEXT.secondary }}>{section.heading}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.BLUE, textAlign: 'center', marginBottom: 14 }}>{section.amount}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {section.allocations.map((a: { label: string; pct: number; color: string }, i: number) => (
              <div key={i} style={{ flex: a.pct, padding: 8, borderRadius: 6, background: workspaceTint(a.color, 10), textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: a.color }}>{a.pct}%</div>
                <div style={{ fontSize: 8, color: WORKSPACE_TEXT.tertiary, marginTop: 2 }}>{a.label}</div>
              </div>
            ))}
          </div>
        </div>
      )
    case 'spreadsheet-table':
      return (
        <div style={{ ...agentBorder, marginBottom: 10 }}>
          {agentTag}
          <div style={{ borderRadius: 8, overflow: 'auto', border: '1px solid rgba(255,255,255,0.06)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{section.headers.map((h: string, i: number) => (
                  <th key={i} style={{ padding: '7px 10px', textAlign: i === 0 ? 'left' : 'right', fontSize: 8, letterSpacing: '1px', color: WORKSPACE_TEXT.tertiary, fontWeight: 700, background: workspaceTint(C.GREEN, 6), borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: WORKSPACE_FONT.mono }}>{h.toUpperCase()}</th>
                ))}</tr>
              </thead>
              <tbody>
                {section.rows.map((row: string[], ri: number) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    {row.map((cell: string, ci: number) => (
                      <td key={ci} style={{
                        padding: '6px 10px', fontSize: 11,
                        textAlign: ci === 0 ? 'left' : 'right',
                        color: ci === 0 ? WORKSPACE_TEXT.secondary : (ri >= 6 ? C.GREEN : WORKSPACE_TEXT.tertiary),
                        fontWeight: ci === 0 ? 500 : (ri >= 7 ? 700 : 400),
                        fontVariantNumeric: 'tabular-nums',
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                      }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    case 'note-tag':
      return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
          {section.tags.map((tag: string, i: number) => (
            <span key={i} style={{ fontSize: 9, padding: '3px 8px', borderRadius: 4, background: workspaceTint(C.AMBER, 10), border: `1px solid ${workspaceTint(C.AMBER, 18)}`, color: C.AMBER, fontWeight: 600 }}>{tag}</span>
          ))}
        </div>
      )
    case 'note-item':
      return (
        <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
          <div style={{ fontSize: 9, color: WORKSPACE_TEXT.muted, fontWeight: 600, width: 36, flexShrink: 0, paddingTop: 1, fontFamily: WORKSPACE_FONT.mono }}>{section.time}</div>
          <div style={{ flex: 1, fontSize: 12, color: WORKSPACE_TEXT.secondary, lineHeight: 1.7 }}>{section.text}</div>
          <div style={{
            fontSize: 7, fontWeight: 700, letterSpacing: '0.5px', flexShrink: 0, paddingTop: 2, fontFamily: WORKSPACE_FONT.mono,
            color: section.priority === 'high' ? C.RED : section.priority === 'medium' ? C.AMBER : C.GREEN,
          }}>
            {section.priority === 'high' ? '● HIGH' : section.priority === 'medium' ? '● MED' : '● LOW'}
          </div>
        </div>
      )
    case 'action-items':
      return (
        <div style={{ ...agentBorder, marginBottom: 10 }}>
          {agentTag}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {section.items.map((item: { text: string; owner: string; due: string; status: string }, i: number) => {
              const sc = item.status === 'urgent' ? C.RED : item.status === 'auto' ? C.GREEN : C.AMBER
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1.5px solid ${workspaceTint(sc, 40)}`,
                    background: item.status === 'auto' ? workspaceTint(C.GREEN, 15) : 'transparent',
                    fontSize: 8, color: item.status === 'auto' ? C.GREEN : 'transparent',
                  }}>{item.status === 'auto' ? '✦' : ''}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: WORKSPACE_TEXT.secondary }}>{item.text}</div>
                    <div style={{ fontSize: 8, color: WORKSPACE_TEXT.muted, marginTop: 1, fontFamily: WORKSPACE_FONT.mono }}>{item.owner} · Due {item.due}</div>
                  </div>
                  <span style={{ fontSize: 7, fontWeight: 700, padding: '2px 5px', borderRadius: 3, letterSpacing: '0.5px', color: sc, background: workspaceTint(sc, 10), fontFamily: WORKSPACE_FONT.mono }}>{item.status.toUpperCase()}</span>
                </div>
              )
            })}
          </div>
        </div>
      )
    default:
      return null
  }
}

// ── The rich example document (DEMO) for a mode — the "reads as designed" filler ──
function DemoDocument({ mode }: { mode: ModeDef }) {
  const content = DEMO_CONTENT[mode.key]
  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          padding: '8px 12px', borderRadius: 8,
          background: workspaceTint(WORKSPACE_ACCENTS.gold, 8),
          border: `1px solid ${workspaceTint(WORKSPACE_ACCENTS.gold, 18)}`,
        }}
      >
        <WorkspaceStatusChip status="draft" label="DEMO" size="sm" />
        <span style={{ fontSize: 10.5, color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.ui, lineHeight: 1.5 }}>
          Sample {mode.label} composition — this is how a built document reads. Hit Build to compose real, editable sections in its place.
        </span>
      </div>

      <div
        style={{
          background: 'rgba(255,255,255,0.015)',
          border: `1px solid ${workspaceTint(mode.accent, 10)}`,
          borderRadius: 14,
          padding: '26px 30px',
          boxShadow: `0 4px 40px rgba(0,0,0,0.3), inset 0 1px 0 ${workspaceTint(mode.accent, 8)}`,
        }}
      >
        {content.sections.map((s, i) => (
          <div key={i} style={{ position: 'relative' }}>
            {i > 0 && (s.type === 'heading' || s.type === 'title') && null}
            <DemoSectionView section={s} modeColor={mode.accent} />
          </div>
        ))}

        {/* Demo agent activity feed — the source's right-side stream, DEMO-labeled */}
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${WORKSPACE_SURFACE.line}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 8, letterSpacing: '0.24em', fontWeight: 700, color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono }}>AGENT ACTIVITY</span>
            <WorkspaceStatusChip status="draft" label="DEMO" size="sm" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {content.agents.map((m, i) => {
              const color = DEMO_AGENTS[m.agent] || mode.accent
              return (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 8, color, marginTop: 2, flexShrink: 0 }}>▸</span>
                  <div>
                    <span style={{ fontSize: 8, color, fontWeight: 700, fontFamily: WORKSPACE_FONT.mono }}>{m.agent}</span>
                    <span style={{ fontSize: 9.5, color: WORKSPACE_TEXT.muted, marginLeft: 5, fontFamily: WORKSPACE_FONT.ui }}>{m.text}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

interface Section {
  id: string
  section_order: number
  section_type: string
  title: string | null
  content_json: Record<string, unknown> | null
  source_agent: string | null
}

function sectionText(s: Section): string {
  const c = s.content_json
  if (!c) return ''
  if (typeof c === 'string') return c
  const cand = (c.text ?? c.body ?? c.markdown ?? c.content) as unknown
  if (typeof cand === 'string') return cand
  try {
    return JSON.stringify(c, null, 2)
  } catch {
    return ''
  }
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

export default function DocumentBuilder() {
  const [workspaces, setWorkspaces] = useState<WorkspaceApiRow[] | null>(null)
  const [active, setActive] = useState<WorkspaceApiRow | null>(null)
  const [sections, setSections] = useState<Section[]>([])
  const [events, setEvents] = useState<WorkspaceEventApiRow[]>([])
  const [view, setView] = useState<'doc' | 'split' | 'preview'>('split')
  const [activeMode, setActiveMode] = useState<ModeKey>('document')
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [running, setRunning] = useState(false)
  const [runSeconds, setRunSeconds] = useState(0)
  const [runNote, setRunNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [newName, setNewName] = useState('')
  const [localEdits, setLocalEdits] = useState<Record<string, string>>({})
  const [stagedEdits, setStagedEdits] = useState<Array<{ id: string; section: string }>>([])

  const signals = useWorkspaceSignals({ limit: 6 })

  const mode = MODE_BY_KEY[activeMode] ?? MODES[0]
  const ACCENT = mode.accent

  // Real build-elapsed timer — counts only while a run is in flight.
  useEffect(() => {
    if (!running) return
    const t = setInterval(() => setRunSeconds((p) => p + 1), 1000)
    return () => clearInterval(t)
  }, [running])

  const guard = useCallback((err: unknown) => {
    if (isAuthError(err)) { setAuthRequired(true); return }
    setError(formatError(err))
  }, [])

  const loadWorkspaces = useCallback(async () => {
    setError(null)
    try {
      const result = await api.workspace.list({ scope: 'personal', limit: 50 })
      setWorkspaces(result.workspaces)
    } catch (err) { guard(err) }
  }, [guard])

  useEffect(() => { void loadWorkspaces() }, [loadWorkspaces])

  const openWorkspace = useCallback(async (ws: WorkspaceApiRow) => {
    setActive(ws)
    setSections([])
    setEvents([])
    setLocalEdits({})
    setStagedEdits([])
    setRunNote(null)
    setError(null)
    if (ws.current_mode && MODE_BY_KEY[ws.current_mode]) setActiveMode(ws.current_mode as ModeKey)
    try {
      const [detail, ev] = await Promise.all([
        api.workspace.get(ws.id),
        api.workspace.events({ workspace_id: ws.id, limit: 30 }),
      ])
      setSections((detail.sections ?? []) as Section[])
      setEvents(ev.events ?? [])
    } catch (err) { guard(err) }
  }, [guard])

  const createWorkspace = useCallback(async () => {
    if (!newName.trim()) return
    setBusy(true)
    try {
      const result = await api.workspace.create({ name: newName.trim(), scope: 'personal', mode: activeMode })
      setNewName('')
      await loadWorkspaces()
      if (result.workspace) void openWorkspace(result.workspace)
    } catch (err) { guard(err) } finally { setBusy(false) }
  }, [newName, activeMode, loadWorkspaces, openWorkspace, guard])

  const build = useCallback(async () => {
    if (!active || !prompt.trim()) return
    setRunning(true)
    setRunSeconds(0)
    setRunNote(null)
    setError(null)
    try {
      const result = await api.workspace.run({ workspaceId: active.id, mode: activeMode, prompt: prompt.trim() })
      const inline = result.dispatch?.inline
      setRunNote(
        result.dispatch?.ok
          ? inline
            ? 'Run dispatched inline — sections update below as the engine composes them.'
            : `Run queued to the provider engine (job ${result.dispatch.provider_job_id ?? '—'}). Sections land as it completes.`
          : `Run recorded but dispatch reported: ${result.dispatch?.error ?? 'no dispatch'}`,
      )
      setPrompt('')
      // Re-read sections + events to reflect whatever the run produced.
      const [detail, ev] = await Promise.all([
        api.workspace.get(active.id),
        api.workspace.events({ workspace_id: active.id, limit: 30 }),
      ])
      setSections((detail.sections ?? []) as Section[])
      setEvents(ev.events ?? [])
    } catch (err) { guard(err) } finally { setRunning(false) }
  }, [active, prompt, activeMode, guard])

  const stageAiEdit = useCallback((section: Section) => {
    setStagedEdits((prev) => [
      { id: `${section.id}-${prev.length}`, section: section.title || `Section ${section.section_order}` },
      ...prev,
    ])
  }, [])

  const orderedSections = useMemo(
    () => [...sections].sort((a, b) => a.section_order - b.section_order),
    [sections],
  )
  const hasReal = orderedSections.length > 0

  const headerStatus = error
    ? 'error'
    : active
      ? running ? ['building'] : ['powered']
      : workspaces !== null ? ['powered'] : undefined

  const mm = String(Math.floor(runSeconds / 60)).padStart(2, '0')
  const ss = String(runSeconds % 60).padStart(2, '0')

  // ── Header chips: build timer + honest (requires-setup) export chips ──
  const headerChips = (
    <>
      {active && <WorkspaceStatusChip status={running ? 'building' : 'ready'} />}
      {active && (
        <span
          title="Build-elapsed timer — counts while a run is in flight"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6,
            background: 'rgba(255,255,255,0.03)', border: `1px solid ${WORKSPACE_SURFACE.lineFaint}`,
            fontFamily: WORKSPACE_FONT.mono, fontSize: 10, fontWeight: 600, color: WORKSPACE_TEXT.tertiary,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 4, height: 4, borderRadius: '50%',
              background: running ? ACCENT : 'rgba(255,255,255,0.2)',
              boxShadow: running ? `0 0 6px ${workspaceTint(ACCENT, 60)}` : 'none',
            }}
          />
          {mm}:{ss}
        </span>
      )}
      {active && hasReal && [mode.ext, '.pdf'].filter((v, i, a) => a.indexOf(v) === i).map((ext) => (
        <span
          key={ext}
          title="Export is not wired yet — no export endpoint exists. This is honestly REQUIRES-SETUP, not a live export."
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6,
            background: workspaceTint(WORKSPACE_ACCENTS.gold, 8), border: `1px solid ${workspaceTint(WORKSPACE_ACCENTS.gold, 22)}`,
            fontFamily: WORKSPACE_FONT.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: WORKSPACE_ACCENTS.gold, cursor: 'not-allowed',
          }}
        >
          Export {ext} · SETUP
        </span>
      ))}
    </>
  )

  // ── EDITOR pane: inline-editable real sections, or DEMO structure ──
  const editorPane = hasReal ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {orderedSections.map((s) => (
        <WorkspaceInlineEditable
          key={s.id}
          label={s.title || `${s.section_type} · ${s.section_order}`}
          accent={ACCENT}
          value={localEdits[s.id] ?? sectionText(s)}
          dirty={localEdits[s.id] != null && localEdits[s.id] !== sectionText(s)}
          onCommit={(next: string) => setLocalEdits((prev) => ({ ...prev, [s.id]: next }))}
          aiAction={{
            label: 'AI edit',
            gateLabel: 'Stages an approval-required edit — never rewrites the text or calls a provider',
            onRequest: () => stageAiEdit(s),
          }}
        />
      ))}
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          fontSize: 12, lineHeight: 1.7, color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.ui,
          padding: '10px 12px', borderRadius: 8,
          background: WORKSPACE_SURFACE.card, border: `1px solid ${WORKSPACE_SURFACE.line}`,
        }}
      >
        No real sections yet. Describe the {mode.label.toLowerCase()} in the command bar below and hit Build — the engine composes editable sections here. The structure below is a DEMO of how the composed document reads.
      </div>
      <DemoDocument mode={mode} />
    </div>
  )

  // ── PREVIEW pane: read-only rendered document, or DEMO structure ──
  const previewPane = hasReal ? (
    <WorkspaceDocumentPane
      accent={ACCENT}
      title={active?.name}
      meta={active ? `${orderedSections.length} sections · ${active.current_mode}` : undefined}
      building={running}
      empty={{ glyph: '▤', title: 'Preview', body: 'Built sections render here as a clean document.' }}
    >
      {orderedSections.map((s) => (
        <WorkspaceDocSection
          key={s.id}
          heading={s.title || undefined}
          agent={s.source_agent ? { name: s.source_agent, accent: ACCENT } : undefined}
        >
          {localEdits[s.id] ?? sectionText(s)}
        </WorkspaceDocSection>
      ))}
    </WorkspaceDocumentPane>
  ) : (
    <DemoDocument mode={mode} />
  )

  return (
    <WorkspaceCommandShell
      kicker="SQUARE CODE · DOCUMENT WORKSPACE"
      title="Document Builder"
      subtitle="A multi-mode window-pane workspace — pick a mode, describe it, the engine composes real sections, and you edit inline across Doc / Split / Preview panes."
      accent={ACCENT}
      status={headerStatus}
      chips={headerChips}
      modes={MODES.map((m) => ({ key: m.key, label: m.label, glyph: m.glyph, accent: m.accent }))}
      activeMode={activeMode}
      onModeChange={(k: string) => setActiveMode(k as ModeKey)}
      actions={
        <>
          {active && <Button variant="secondary" size="sm" onClick={() => setActive(null)}>← Documents</Button>}
          <Button variant="secondary" size="sm" onClick={() => void loadWorkspaces()}>Refresh</Button>
        </>
      }
      rail={
        <WorkspaceRightRail>
          <WorkspaceRailSection label="AGENT ACTIVITY" accent={ACCENT} badge={events.length > 0 ? events.length : undefined}>
            {events.length === 0 ? (
              <div style={{ fontSize: 11, lineHeight: 1.6, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.ui }}>
                Agent events from a Build run stream here — real sq_workspace_agent_events, not a demo.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {events.slice(0, 12).map((e) => (
                  <div key={e.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 8, color: ACCENT, marginTop: 3, flexShrink: 0 }}>▸</span>
                    <div>
                      <span style={{ fontSize: 9, fontWeight: 700, color: ACCENT, fontFamily: WORKSPACE_FONT.mono }}>{e.agent_name}</span>
                      <span style={{ fontSize: 10.5, color: WORKSPACE_TEXT.tertiary, marginLeft: 5, fontFamily: WORKSPACE_FONT.ui }}>{e.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </WorkspaceRailSection>

          <WorkspaceRailSection label="STAGED AI EDITS" accent={WORKSPACE_ACCENTS.purple}>
            <WorkspaceProofPanel
              title="APPROVAL QUEUE"
              rows={
                stagedEdits.length === 0
                  ? [{ id: 'none', label: 'No staged edits', status: 'idle', detail: 'Request an AI edit on a section to stage it here — approval-gated, never auto-applied.' }]
                  : stagedEdits.map((e) => ({
                      id: e.id,
                      label: e.section,
                      status: 'approval_required',
                      detail: 'Prepared — an operator approval flow would apply this. No text was rewritten and no provider was called.',
                    }))
              }
              sourceLabel="prepared-not-applied · approval-gated"
            />
          </WorkspaceRailSection>

          <WorkspaceRailSection label="SIGNAL" accent={WORKSPACE_ACCENTS.cyan}>
            <WorkspaceNotificationRail
              title="LIVE"
              items={signals.items}
              channels={signals.channels}
              loading={signals.loading}
              live={signals.live}
              compact
              filterable={false}
              maxItems={5}
              sourceLabel={signals.sourceLabel}
              emptyTitle="No signal yet"
              emptyBody="Activation, gates, and receipts land here."
            />
          </WorkspaceRailSection>
        </WorkspaceRightRail>
      }
      composer={active && !authRequired ? (
        <WorkspaceActionComposer
          accent={ACCENT}
          value={prompt}
          onChange={setPrompt}
          onSubmit={prompt.trim() ? () => void build() : undefined}
          busy={running}
          submitLabel="Build"
          busyLabel="Building"
          placeholder={DEMO_CONTENT[activeMode].placeholder}
          attachments={[
            { key: 'file', label: '📎 File', disabled: true, hint: 'Attachment intake not wired yet — coming later' },
            { key: 'voice', label: '🎤 Voice', disabled: true, hint: 'Voice capture not wired yet — coming later' },
            { key: 'video', label: '🎥 Video', disabled: true, hint: 'Video intake not wired yet — coming later' },
            { key: 'url', label: '🔗 URL', disabled: true, hint: 'URL intake not wired yet — coming later' },
            { key: 'template', label: '📋 Template', disabled: true, hint: 'Templates not wired yet — coming later' },
          ]}
          gateLabel="Build dispatches a governed workspace run; inline edits stay local and AI edits stage for approval"
        />
      ) : undefined}
    >
      {error && <div style={{ marginBottom: 14 }}><Banner severity="warning" title="Document Builder">{error}</Banner></div>}
      {runNote && <div style={{ marginBottom: 14 }}><InlineAlert severity="info">{runNote}</InlineAlert></div>}

      {authRequired ? (
        <EmptyState message="Sign in to continue — Document Builder is backed by your sq_workspaces and needs an authenticated session." />
      ) : !active ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <section
            style={{
              padding: '14px 16px',
              borderRadius: 12,
              background: 'var(--sc-workspace-card, rgba(255,255,255,0.02))',
              border: '1px solid var(--sc-workspace-line, rgba(255,255,255,0.07))',
            }}
          >
            <div style={{ fontSize: 9, letterSpacing: '0.26em', fontWeight: 700, textTransform: 'uppercase', color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono, marginBottom: 10 }}>
              {`New ${activeMode === 'document' ? 'document' : mode.label.toLowerCase()} workspace`}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={`${mode.label} name — e.g. Silver Key Partnership Brief`}
                style={{
                  flex: '1 1 260px',
                  padding: '8px 12px',
                  fontSize: 12.5,
                  background: 'rgba(255,255,255,0.03)',
                  color: WORKSPACE_TEXT.primary,
                  border: `1px solid ${workspaceTint(ACCENT, 14)}`,
                  borderRadius: 8,
                  outlineColor: ACCENT,
                  fontFamily: WORKSPACE_FONT.ui,
                  minWidth: 0,
                }}
              />
              <Button onClick={() => void createWorkspace()} loading={busy} disabled={busy || !newName.trim()}>
                Create {mode.label.toLowerCase()}
              </Button>
            </div>
          </section>

          {workspaces === null && <InlineAlert severity="info">Loading document workspaces…</InlineAlert>}
          {workspaces !== null && workspaces.length === 0 && (
            <EmptyState message="No document workspaces yet. Create one above to open the window-pane editor." />
          )}
          {workspaces !== null && workspaces.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {workspaces.map((ws) => {
                const wm = MODE_BY_KEY[ws.current_mode] ?? mode
                return (
                  <WorkspaceModuleCard
                    key={ws.id}
                    glyph={wm.glyph}
                    accent={wm.accent}
                    title={ws.name}
                    status={ws.status === 'building' ? 'building' : 'ready'}
                    summary={`${ws.current_mode} workspace`}
                    footnote={`updated ${new Date(ws.updated_at).toLocaleDateString()}`}
                    onOpen={() => void openWorkspace(ws)}
                    actions={<Button variant="secondary" size="sm" onClick={() => void openWorkspace(ws)}>Open</Button>}
                  />
                )
              })}
            </div>
          )}

          {/* Structured preview region — the workspace reads as designed even with no run */}
          <section
            style={{
              padding: '14px 16px',
              borderRadius: 12,
              background: 'var(--sc-workspace-card, rgba(255,255,255,0.02))',
              border: '1px solid var(--sc-workspace-line, rgba(255,255,255,0.07))',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 9, letterSpacing: '0.26em', fontWeight: 700, textTransform: 'uppercase', color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono }}>
                {mode.label} preview
              </span>
              <WorkspaceStatusChip status="draft" label="DEMO" size="sm" />
            </div>
            <DemoDocument mode={mode} />
          </section>
        </div>
      ) : (
        <WorkspaceDualPane
          accent={ACCENT}
          view={view}
          onViewChange={(v: string) => setView(v as 'doc' | 'split' | 'preview')}
          leftLabel="EDITOR · INLINE"
          rightLabel="PREVIEW"
          left={editorPane}
          right={previewPane}
          minHeight={360}
        />
      )}
    </WorkspaceCommandShell>
  )
}
